// @ts-check
// Finalidade do número em Configurações > Canais (contrato 10 de atendimento, seções 4.1 e 24.4): Captação, Atendimento
// ou Misto, com o departamento padrão da fila nos dois últimos; só com o módulo ligado. Mudar pede WORKSPACE e
// SERVICE_DESK_SETTINGS e vale só para mensagens novas; quem não pode mudar vê a finalidade sem editar.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { updateChannelPurpose } from '../../data/service-desk.js';
import { CHANNEL_PURPOSES } from '../../../../shared/service-desk/ticket-values.js';
import { SegmentedControl } from '../../ui/segmented-control.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { serviceErrorMessage } from './service-display.js';
import { t } from '../../i18n/index.js';

/**
 * @param {{ channel: { id: string, settings?: Record<string, any> | null }, teams: Array<{ id: string, name: string, teamKind: string, isActive?: boolean }>,
 *   canEdit: boolean, onChanged: () => void }} props
 */
export function ChannelPurposeField({ channel, teams, canEdit, onChanged }) {
  const savedPurpose = String(channel.settings?.purpose ?? 'CAPTURE');
  const savedTeamId = channel.settings?.serviceTeamId ?? '';
  const [purpose, setPurpose] = useState(savedPurpose);
  const [teamId, setTeamId] = useState(savedTeamId);
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const standardTeams = teams.filter((team) => team.teamKind === 'STANDARD' && team.isActive !== false);
  const needsTeam = purpose !== 'CAPTURE';
  const effectiveTeamId = needsTeam ? teamId || standardTeams[0]?.id || '' : '';
  const isChanged = purpose !== savedPurpose || (needsTeam && effectiveTeamId !== savedTeamId);
  const save = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await updateChannelPurpose(channel.id, { purpose: /** @type {any} */ (purpose), ...(needsTeam ? { serviceTeamId: effectiveTeamId } : {}) });
      showSuccessSnackbar(t('serviceSettings.channelPurpose.saved'));
      onChanged();
    } catch (requestError) {
      setError(serviceErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };
  return html`<div class="channel-card-section service-channel-purpose" data-testid="channel-purpose">
    <span class="form-field-label">${t('serviceSettings.channelPurpose.label')}</span>
    <${SegmentedControl} label=${t('serviceSettings.channelPurpose.label')} value=${purpose} disabled=${!canEdit} name=${`purpose-${channel.id}`}
      onChange=${(value) => setPurpose(value)}
      options=${CHANNEL_PURPOSES.map((value) => ({ value, label: t(`serviceSettings.channelPurpose.values.${value}`), hint: t(`serviceSettings.channelPurpose.hints.${value}`) }))} />
    <span class="input-hint">${t(`serviceSettings.channelPurpose.hints.${purpose}`)}</span>
    ${needsTeam
      ? html`<label class="form-field"><span class="form-field-label">${t('serviceSettings.channelPurpose.team')}</span>
          <select class="text-input" value=${effectiveTeamId} disabled=${!canEdit} onChange=${(/** @type {Event} */ event) => setTeamId(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
            ${standardTeams.map((team) => html`<option key=${team.id} value=${team.id}>${team.name}</option>`)}
          </select></label>`
      : null}
    ${error ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${error}</span></div>` : null}
    ${canEdit && isChanged
      ? html`<div class="service-inline-actions">
          <span class="input-hint">${t('serviceSettings.channelPurpose.onlyNewMessages')}</span>
          <${Button} title=${t('serviceSettings.save')} size="small" accent="blue" isLoading=${isSaving} disabled=${needsTeam && !effectiveTeamId} onClick=${save} />
        </div>`
      : null}
    ${!canEdit ? html`<span class="input-hint">${t('serviceSettings.channelPurpose.readOnly')}</span>` : null}
  </div>`;
}
